<template>
    <div class="crud-table mt-3">

        <!-- create-new button (if enabled) -->

        <b-btn class="float-right" variant="primary"
               v-if="newItem"
               @click="createItem()">
            {{ $i18n.gt('LABEL_CREATE_NEW') }} {{ itemName }}
        </b-btn>

        <h1>{{ title }}</h1>

        <data-table ref="dataTable"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="showDetails"
                    :get-items="getItems"
                    :filters="filters"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'actions'">
                    <b-btn class="btn btn-secondary btn-sm mr-2"
                           @click="editItem(slotProps.row.item)">
                        <shs-icon name="pencil"></shs-icon>
                    </b-btn>
                    <b-btn size="sm"
                           variant="danger"
                           @click="deleteItem(slotProps.row.item)">
                        <shs-icon name="delete"></shs-icon>
                    </b-btn>
                </div>
            </template>

            <!-- template for custom row details -->
            <template v-slot:customRowDetails="slotProps">
                <slot name="customRowDetails" v-bind:row="slotProps.row">
                    <!-- parent component can customize this via a template, same as in DataTable... -->
                </slot>
            </template>

        </data-table>

        <!-- edit modal-->

        <b-modal hide-footer
                 id="editModal"
                 :title="`${editMode == 'UPDATE' ? $i18n.gt('LABEL_UPDATE') : $i18n.gt('LABEL_CREATE')} ${itemName}`">

            <!-- edit fields -->

            <b-form-group v-for="(f,i) in editFields" :key="i"
                          :label="f.label">

                <!-- text input -->
                <b-form-input v-if="f.type == 'text'"
                              type="text"
                              v-model="editingItem[f.key]">
                </b-form-input>

                <!-- number input -->
                <b-form-input v-if="f.type == 'number'"
                              type="number"
                              v-model="editingItem[f.key]">
                </b-form-input>

                <!-- email input -->
                <b-form-input v-if="f.type == 'email'"
                              type="email"
                              v-model="editingItem[f.key]">
                </b-form-input>

                <!-- textarea -->
                <b-form-textarea v-if="f.type == 'textarea'"
                                 v-model="editingItem[f.key]">
                </b-form-textarea>

                <!-- select -->
                <b-form-select v-if="f.type == 'select'"
                               :options="f.options"
                               v-model="editingItem[f.key]">
                </b-form-select>

            </b-form-group>

            <b-btn variant="primary" class="float-right"
                   @click="saveEditingItem()">
                {{ $i18n.gt('LABEL_SAVE') }}
            </b-btn>
        </b-modal>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

/**
 * Builds on DataTable to provide a complete CRUD view for simple admin-type operations on a given model
 * - including a standard Actions column and a modal for creating, updating or deleting items.
 * NB: Does not include any validation in the modal. Custom column templates are also not supported.
 */

import Vue from 'vue'
import * as _ from 'lodash'

export default Vue.extend({
    name: 'CrudTable',
    props: {
        title: String,
        itemTable: String,
        itemName: String,
        newItem: Object,
        showDetails: Boolean,
        filters: Object,
        displayFields: Array,
        editFields: Array,
    },
    data() {
        return {
            editingItem: {},
            editMode: null, // 'UPDATE' / 'CREATE'
            fields: _.concat(this.displayFields, [
                {key: 'actions', sortable: false, custom: true},
            ]),
        }
    },
    methods: {
        getItems(listParams) {
            return this.$api.getItems(this, this.itemTable, this.filters, listParams)
        },
        createItem() {
            this.editingItem = _.cloneDeep(this.newItem)
            this.editMode = 'CREATE'
            this.$bvModal.show('editModal')
        },
        editItem(item) {
            this.editingItem = item
            this.editMode = 'UPDATE'
            this.$bvModal.show('editModal')
        },
        async saveEditingItem() {
            if (this.editMode == 'UPDATE') {
                try {
                    await this.$api.updateItem(this, this.itemTable, this.editingItem)
                    this.$bvModal.hide('editModal')
                    this.refresh();
                    this.$toasted.show(`${this.itemName} updated`, {duration: 3000})
                } catch (e) {
                    this.$utils.handleError(e, `Could not update ${this.itemName}`)
                }
            } else if (this.editMode == 'CREATE') {
                try {
                    await this.$api.createItem(this, this.itemTable, this.editingItem)
                    this.$bvModal.hide('editModal')
                    this.refresh();
                    this.$toasted.show(`${this.itemName} created`, {duration: 3000})
                } catch (e) {
                    this.$utils.handleError(e, `Could not create ${this.itemName}`)
                }
            }
        },
        async deleteItem(item) {
            if (await this.$bvModal.msgBoxConfirm(`Delete this ${this.itemName} permanently?`)) {
                try {
                    await this.$api.deleteItem(this, this.itemTable, item)
                    this.$toasted.show(`${this.itemName} deleted`, {duration: 3000})
                    this.$refs.dataTable.refresh();
                } catch (e) {
                    this.$utils.handleError(e, `Could not delete ${this.itemName}`)
                }
            }
        },
        refresh() {
            this.$refs.dataTable.refresh()
        },
    },
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
</style>
